import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { formatMoneyDecimal } from "../lib/categories";
import SmartImg from "../components/SmartImg";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";

export default function Cart() {
  const { items, subtotal, updateQty, removeItem, itemCount } = useCart();
  const navigate = useNavigate();
  const shippingFee = itemCount > 0 ? 0 : 0; // "imaginary free shipping"
  const tax = Math.round(subtotal * 0.08 * 100) / 100;
  const total = Math.round((subtotal + shippingFee + tax) * 100) / 100;

  if (items.length === 0) {
    return (
      <main className="mx-auto max-w-[900px] px-5 lg:px-10 py-24 text-center" data-testid="cart-empty">
        <div className="mx-auto inline-flex h-20 w-20 items-center justify-center rounded-full border border-neutral-300 mb-6">
          <ShoppingBag size={28} strokeWidth={1.25} />
        </div>
        <h1 className="font-display font-bold text-3xl tracking-tight">Your cart is empty</h1>
        <p className="text-neutral-600 mt-3">
          Nothing to buy. Nothing to regret buying. Let&apos;s fix that.
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-800 transition-colors"
          data-testid="cart-empty-continue-shopping"
        >
          Start shopping
          <ArrowRight size={16} />
        </Link>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-[1400px] px-5 lg:px-10 py-10" data-testid="cart-page">
      <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tighter mb-8">
        Your cart
      </h1>

      <div className="grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-8 space-y-4" data-testid="cart-items">
          {items.map((item) => (
            <div
              key={item.productId}
              className="flex flex-col sm:flex-row gap-4 border border-neutral-200 rounded-xl p-4 bg-white"
              data-testid={`cart-item-${item.productId}`}
            >
              <Link
                to={`/product/${item.productId}`}
                className="shrink-0 aspect-[4/3] sm:aspect-square sm:w-32 rounded-lg overflow-hidden bg-neutral-100"
              >
                <SmartImg
                  src={item.image}
                  alt={item.title}
                  className="h-full w-full"
                />
              </Link>
              <div className="flex-1 flex flex-col">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-neutral-500">
                      {item.categoryLabel}
                    </div>
                    <Link
                      to={`/product/${item.productId}`}
                      className="font-display font-semibold text-neutral-900 hover:underline underline-offset-4"
                    >
                      {item.title}
                    </Link>
                  </div>
                  <div className="font-display font-bold text-lg text-neutral-900 whitespace-nowrap">
                    {formatMoneyDecimal(item.price * item.quantity)}
                  </div>
                </div>

                <div className="mt-auto pt-4 flex items-center justify-between">
                  <div className="inline-flex items-center h-10 rounded-full border border-neutral-300 overflow-hidden">
                    <button
                      onClick={() => updateQty(item.productId, item.quantity - 1)}
                      className="h-full w-9 inline-flex items-center justify-center hover:bg-neutral-100 transition-colors"
                      data-testid={`cart-decrement-${item.productId}`}
                    >
                      <Minus size={14} />
                    </button>
                    <span
                      className="w-8 text-center text-sm font-medium"
                      data-testid={`cart-qty-${item.productId}`}
                    >
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => updateQty(item.productId, item.quantity + 1)}
                      className="h-full w-9 inline-flex items-center justify-center hover:bg-neutral-100 transition-colors"
                      data-testid={`cart-increment-${item.productId}`}
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  <button
                    onClick={() => removeItem(item.productId)}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-sm text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
                    data-testid={`cart-remove-${item.productId}`}
                  >
                    <Trash2 size={14} />
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <aside className="col-span-12 lg:col-span-4">
          <div className="border border-neutral-200 rounded-xl p-6 bg-white lg:sticky lg:top-32" data-testid="cart-summary">
            <h2 className="font-display font-bold text-xl tracking-tight">Order summary</h2>
            <dl className="mt-5 space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-neutral-600">Subtotal</dt>
                <dd className="font-medium text-neutral-900" data-testid="cart-subtotal">
                  {formatMoneyDecimal(subtotal)}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-neutral-600">Shipping</dt>
                <dd className="font-medium text-neutral-900">Free (imaginary)</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-neutral-600">Tax (8%)</dt>
                <dd className="font-medium text-neutral-900">{formatMoneyDecimal(tax)}</dd>
              </div>
            </dl>
            <div className="mt-5 pt-5 border-t border-neutral-200 flex justify-between items-baseline">
              <span className="font-display font-semibold text-neutral-900">Total</span>
              <span className="font-display font-bold text-2xl" data-testid="cart-total">
                {formatMoneyDecimal(total)}
              </span>
            </div>
            <button
              onClick={() => navigate("/checkout")}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-800 transition-colors"
              data-testid="checkout-button"
            >
              Proceed to Checkout
              <ArrowRight size={16} />
            </button>
            <Link
              to="/"
              className="mt-3 block text-center text-sm text-neutral-600 hover:text-neutral-900 hover:underline underline-offset-4"
              data-testid="continue-shopping-link"
            >
              Continue shopping
            </Link>
          </div>
        </aside>
      </div>
    </main>
  );
}
